/**
 * Suggested late_joiner_penalty so effective games (actual + penalty) catches up to the field.
 * Subtracts games the player has already played (e.g. returning from pause).
 *
 * @param {{ averageGames: number, playerGamesPlayed?: number }} params
 * @returns {{ suggestedPenalty: number, shouldShowModal: boolean, playerGamesPlayed: number, isReturningPlayer: boolean }}
 */
export function computeLateJoinerPenaltySuggestion({ averageGames, playerGamesPlayed = 0 }) {
  const played = Math.max(0, Number(playerGamesPlayed) || 0);
  const avg = Number(averageGames) || 0;
  const shouldShowModal = avg >= 1;
  const suggestedPenalty = shouldShowModal
    ? Math.max(0, Math.round(avg) - played)
    : 0;

  return {
    suggestedPenalty,
    shouldShowModal,
    playerGamesPlayed: played,
    isReturningPlayer: played > 0,
  };
}

function emptyLatePenaltySuggestion(playerGamesPlayed = 0) {
  const played = Math.max(0, Number(playerGamesPlayed) || 0);
  return {
    suggestedPenalty: 0,
    averageGames: 0,
    shouldShowModal: false,
    playerGamesPlayed: played,
    isReturningPlayer: played > 0,
  };
}

function rosterIdsFromHistoryEntry(entry) {
  const team1 = entry?.team1 || entry?.teams?.[0] || [];
  const team2 = entry?.team2 || entry?.teams?.[1] || [];
  const players = entry?.players || [];
  return [...team1, ...team2, ...players].filter(Boolean);
}

function countPlayerGamesInHistory(matchHistory, playerId) {
  if (!playerId) return 0;
  return (matchHistory || []).reduce((count, entry) => (
    rosterIdsFromHistoryEntry(entry).includes(playerId) ? count + 1 : count
  ), 0);
}

function actualGamesPlayed(playerId, playersById, matchHistory) {
  const stored = Number(playersById?.[playerId]?.gamesPlayedThisSession) || 0;
  return Math.max(stored, countPlayerGamesInHistory(matchHistory, playerId));
}

/**
 * Same suggestion as getSuggestedLatePenalty / compute_late_joiner_penalty_suggestion,
 * from live session state so hosts can still apply a late penalty while offline.
 */
export function getSuggestedLatePenaltyFromLiveState({
  playerId,
  sessionPlayerStatus = {},
  playersById = {},
  sessionPlayerPenalties = {},
  matchHistory = [],
} = {}) {
  const playerGamesPlayed = actualGamesPlayed(playerId, playersById, matchHistory);
  const activeIds = Object.keys(sessionPlayerStatus).filter(
    (id) => sessionPlayerStatus[id] === 'active' && id !== playerId
  );

  if (activeIds.length === 0) {
    return emptyLatePenaltySuggestion(playerGamesPlayed);
  }

  const hasSessionGames = (matchHistory || []).length > 0
    || activeIds.some((id) => actualGamesPlayed(id, playersById, matchHistory) > 0);

  if (!hasSessionGames) {
    return emptyLatePenaltySuggestion(playerGamesPlayed);
  }

  const totalEffective = activeIds.reduce((sum, id) => {
    const games = actualGamesPlayed(id, playersById, matchHistory);
    const penalty = Number(
      sessionPlayerPenalties[id] ?? playersById[id]?.lateJoinerPenalty
    ) || 0;
    return sum + games + penalty;
  }, 0);
  const averageGames = totalEffective / activeIds.length;
  const suggestion = computeLateJoinerPenaltySuggestion({ averageGames, playerGamesPlayed });

  return {
    ...suggestion,
    averageGames,
  };
}
